import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiLock, FiMail, FiArrowLeft, FiUserPlus, FiEye, FiEyeOff, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../utils/auth';
import { ensureValidTenantId } from '../utils/auth';
import Modal from '../components/Modals/Modal';
import Googlelogin from './Googlelogin';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TenantPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear login error when user starts typing
    if (loginError) {
      setLoginError('');
    }
  };

  const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&#]{8,}$/;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!strongPasswordRegex.test(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters, include uppercase, lowercase, number, and special character.';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoginError('');
    setIsLoading(true);
    try {
      // Try to find account by email first
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email,
          password: formData.password 
        })
      });
      if (!res.ok) {
        setLoginError('Invalid email or password.');
        return;
      }
      const loginData = await res.json();
      // Store accountId returned by backend for subsequent API calls
      if (loginData.accountId) {
        localStorage.setItem('tenantId', loginData.accountId);
      }
      login(loginData.access_token, {
        email: formData.email,
        name: formData.email.split('@')[0]
      });
      
      // Check onboarding status after successful login
      try {
        const onboardingRes = await fetch(`${API_BASE_URL}/api/accounts/onboarding/status`, {
          headers: {
            'Authorization': `Bearer ${loginData.access_token}`
          }
        });
        
        if (onboardingRes.ok) {
          const isOnboardingCompleted = await onboardingRes.json();
          if (isOnboardingCompleted) {
            navigate('/dashboard');
          } else {
            navigate('/onboarding');
          }
        } else {
          // If we can't check onboarding status, assume it's not completed
          navigate('/onboarding');
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        navigate('/onboarding');
      }
    } catch (err) {
      setLoginError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="brand-logo">
            <h2>Hamara Bill</h2>
            <p className="brand-subtitle">By TFS Traders</p>
          </div>
          <h1>Welcome Back</h1>
          <p className="text-muted">Sign in to continue to Bill Book</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <div className="input-group">
              <FiMail className="input-icon" />
              <input
                type="email"
                name="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your mail"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="input-group">
              <FiLock className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Enter Your Password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          {loginError && (
            <div className="error-message text-center mb-3">
              {loginError}
            </div>
          )}

          <div className="form-options">
            <label className="checkbox-label">
              <input type="checkbox" />
              <span className="checkmark"></span>
              Remember me
            </label>
            <Link to="/forgot-password" className="forgot-password">
              Forgot Password?
            </Link>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FiRefreshCw className="animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <Googlelogin />

          <p className="text-center text-muted mt-4">
            Don't have an account? <button type="button" className="link-primary" onClick={() => {
              navigate('/register');
            }}>Create one</button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default TenantPage;